<template>
  <div class="auth-page">
    <div class="auth-card">
      <!-- Header -->
      <div class="auth-header">
        <div class="auth-icon-wrap">
          <v-icon color="white" size="28">mdi-account-plus-outline</v-icon>
        </div>
        <h1 class="auth-title">{{ $t('register') }}</h1>
        <p class="auth-subtitle">{{ $t('login.access') }}</p>
      </div>

      <v-alert type="error" v-if="errorSummary !== null" class="auth-alert" dense rounded="lg">
        {{ errorSummary }}
      </v-alert>

      <!-- Form -->
      <v-form
        v-model="valid"
        ref="form"
        :lazy-validation="false"
        @keyup.native.enter="submit"
        class="auth-form"
      >
        <v-text-field
          :label="$t('username')"
          v-model="user.username"
          placeholder="Enter Your Nickname"
          type="text"
          outlined
          dense
          rounded
          :rules="usernameRules"
          prepend-inner-icon="mdi-account-outline"
          class="auth-input"
        ></v-text-field>

        <v-text-field
          label="Email"
          v-model="user.email"
          placeholder="Enter Email"
          type="email"
          outlined
          dense
          rounded
          :rules="emailRules"
          prepend-inner-icon="mdi-email-outline"
          class="auth-input"
        ></v-text-field>

        <v-text-field
          :label="$t('password.title')"
          :placeholder="$t('password.title')"
          :rules="passwordRules"
          :append-icon="value ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
          :type="value ? 'password' : 'text'"
          v-model="user.password"
          outlined
          dense
          rounded
          error-count="3"
          @click:append="() => (value = !value)"
          prepend-inner-icon="mdi-lock-outline"
          class="auth-input"
        ></v-text-field>

        <v-text-field
          :label="$t('password.confirm')"
          v-model="user.confirmPassword"
          type="password"
          outlined
          dense
          rounded
          :placeholder="$t('password.confirm')"
          :rules="confirmPasswordRules.concat(passwordConfirmationRule)"
          prepend-inner-icon="mdi-lock-check-outline"
          class="auth-input"
        ></v-text-field>
        <v-text-field
          label="Invitation Code"
          v-model="user.invitationCode"
          placeholder="Enter invitation code"
          type="text"
          outlined
          dense
          rounded
          :rules="invitationCodeRules"
          prepend-inner-icon="mdi-key-variant"
          class="auth-input"
        ></v-text-field>
      </v-form>

      <!-- Actions -->
      <div class="auth-actions">
        <v-btn to="/" class="auth-btn-back" rounded outlined>
          <v-icon left size="18">mdi-arrow-left</v-icon>
          {{ $t('back') }}
        </v-btn>
        <v-btn class="auth-btn-submit" rounded dark @click="submit" :disabled="!valid || loading">
          <v-icon left size="18">mdi-account-check-outline</v-icon>
          {{ $t('register') }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import util from '@/mixins/util';
import EventBus from '@/helpers/event-bus';
import i18n from '@/i18n';

export default {
  name: 'SignUp',
  mixins: [util],
  data: () => ({
    loading: false,
    valid: false,
    value: true,
    user: {
      username: null,
      email: null,
      password: null,
      confirmPassword: null,
      realmRoles: [],
      invitationCode: null,
    },
    usernameRules: [
      v => !!v || i18n.t('nickname-required'),
      v => /^(?=.{4})[a-z\d]*_?[a-z\d]+$/i.test(v) || i18n.t('nickname-rule'),
    ],
    emailRules: [
      v => !!v || i18n.t('email-required'),
      v => /.+@.+\..+/.test(v) || i18n.t('email-valid'),
    ],
    passwordRules: [
      v => !!v || i18n.t('password.required'),
      v =>
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/.test(v) ||
        i18n.t('password.rule-message'),
    ],
    confirmPasswordRules: [v => !!v || i18n.t('password.confirm-required')],
    invitationCodeRules: [v => !!v || i18n.t('An invitation code is required to sign-up')],
    responseStatus: null,
    errorSummary: null,
  }),
  methods: {
    submit() {
      console.log('[LogIn] Trying to sign up...');
      this.loading = true;
      EventBus.$emit('waiting', true);
      // Keycloak makes usernames toLowerCase
      this.user.username = this.user.username.toLowerCase();

      this.axios
        .post('/api/v1/auth/add-user', this.user)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('register-success'),
            color: 'success',
          });
          this.$router.push(
            this.$route.query.redirect || {
              name: 'LogIn',
            },
          );
        })
        .catch(error => {
          console.log(error);
          this.errorSummary = this.$i18n.t('errorlist.try-again');
          if (error.response && error.response.status === 409) {
            EventBus.$emit('snackbar', {
              text: error.response.data,
              color: 'error',
            });
          }
        })
        .finally(() => {
          this.loading = false;
          EventBus.$emit('waiting', false);
        });
    },
  },
  computed: {
    passwordConfirmationRule() {
      return () =>
        this.user.password === this.user.confirmPassword || i18n.t('password.matching-rule');
    },
  },
};
</script>

<style lang="scss" scoped>
.auth-page {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 160px);
  padding: 40px 16px;
  background: linear-gradient(160deg, #f0f4f8 0%, #e8edf5 50%, #f5f1f8 100%);
}

.auth-card {
  width: 100%;
  max-width: 460px;
  background: #ffffff;
  border-radius: 20px;
  padding: 40px 36px 32px;
  box-shadow: 0 8px 40px rgba(26, 35, 126, 0.08), 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.auth-header {
  text-align: center;
  margin-bottom: 28px;
}

.auth-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  box-shadow: 0 4px 16px rgba(123, 31, 162, 0.25);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a237e;
  margin-bottom: 4px;
}

.auth-subtitle {
  font-size: 0.92rem;
  color: #757575;
  margin: 0;
}

.auth-alert {
  margin-bottom: 20px;
}

.auth-form {
  margin-bottom: 8px;
}

.auth-input {
  margin-bottom: 4px;
}

.auth-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
}

.auth-btn-back {
  flex: 1;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  border-color: #bdbdbd !important;
  color: #616161 !important;
  transition: all 0.3s ease !important;
}

.auth-btn-back:hover {
  border-color: #1a237e !important;
  color: #1a237e !important;
  background: rgba(26, 35, 126, 0.04) !important;
}

.auth-btn-submit {
  flex: 1;
  background: linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%) !important;
  text-transform: none !important;
  font-weight: 700 !important;
  letter-spacing: 0.3px !important;
  box-shadow: 0 4px 16px rgba(123, 31, 162, 0.25) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.auth-btn-submit:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 24px rgba(123, 31, 162, 0.35) !important;
}
.input-field :deep(.v-field__outline) {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.input-field:hover :deep(.v-field__outline) {
  border-color: rgba(0, 0, 0, 0.15) !important;
}
</style>
